<template>
    <div class="pa-root">

        <div v-if="loading" class="pa-center">
            <div class="pa-spinner"></div>
        </div>

        <div v-else-if="!authorized" class="pa-center">
            <div class="pa-lock-card">
                <div v-if="!signedIn">
                    <div style="font-size:48px">🔐</div>
                    <h2>Connexion requise</h2>
                    <p>Connecte-toi avec Google pour accéder à l'administration.</p>
                    <button class="pa-btn-google" :disabled="authLoading" @click="signIn">
                        {{ authLoading ? 'Connexion…' : 'Se connecter avec Google' }}
                    </button>
                    <p v-if="authError" class="pa-error">Échec de la connexion, réessaie.</p>
                </div>
                <div v-else>
                    <div style="font-size:48px">🔒</div>
                    <h2>Accès réservé</h2>
                    <p>Le compte {{ currentEmail }} n'a pas accès à cette page.</p>
                    <button class="pa-btn-google" @click="signIn">Se connecter avec un autre compte</button>
                </div>
            </div>
        </div>

        <div v-else class="pa-body">
            <div class="pa-header">
                <div class="pa-title">🎧 Sons de la page 210</div>
                <div class="pa-header-actions">
                    <a href="/210" target="_blank" class="pa-btn-preview">👁 Voir la page</a>
                    <button class="pa-btn-save" @click="saveAll" :disabled="saving">
                        {{ saving ? '⏳ Sauvegarde…' : '💾 Sauvegarder' }}
                    </button>
                </div>
            </div>

            <div class="pa-hint">
                Ajoute, édite ou réordonne les sons. N'oublie pas de cliquer sur "Sauvegarder".
            </div>

            <div class="pa-debug">Connecté en tant que : {{ currentEmail }} (UID : {{ currentUid }})</div>

            <div v-if="toast" class="pa-toast">✅ Sauvegardé</div>

            <div class="pa-list">
                <div v-for="(artwork, index) in artworks" :key="index" class="pa-card">
                    <div class="pa-order">
                        <button class="pa-btn-order" @click="moveUp(index)" :disabled="index === 0" title="Monter">▲</button>
                        <button class="pa-btn-order" @click="moveDown(index)" :disabled="index === artworks.length - 1" title="Descendre">▼</button>
                    </div>

                    <div class="pa-fields">
                        <label>Nom</label>
                        <input v-model="artwork.label" placeholder="Nom du son" />

                        <label>Lien audio</label>
                        <input v-model="artwork.audio" placeholder="https://..." />
                    </div>

                    <button class="pa-btn-remove" @click="removeArtwork(index)" title="Supprimer">🗑️</button>
                </div>
            </div>

            <button class="pa-btn-add" @click="addArtwork">➕ Ajouter un son</button>
        </div>
    </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/database";

const SUPER_ADMINS = ["ephraimichael@gmail.com", "eifoany@gmail.com"];

export default {
    data() {
        return {
            authorized: false,
            signedIn: false,
            loading: true,
            authLoading: false,
            authError: false,
            saving: false,
            toast: false,
            artworks: [],
            currentEmail: "",
            currentUid: "",
        };
    },

    async mounted() {
        firebase.auth().onAuthStateChanged(async (user) => {
            this.signedIn = !!user;
            if (!user || !SUPER_ADMINS.includes(user.email)) {
                this.currentEmail = user ? user.email : "";
                this.authorized = false;
                this.loading = false;
                return;
            }
            this.authorized = true;
            this.currentEmail = user.email;
            this.currentUid = user.uid;
            await this.loadArtworks();
            this.loading = false;
        });
    },

    methods: {
        async signIn() {
            this.authLoading = true;
            this.authError = false;
            const provider = new firebase.auth.GoogleAuthProvider();
            try {
                await firebase.auth().signInWithPopup(provider);
            } catch {
                this.authError = true;
            }
            this.authLoading = false;
        },

        async loadArtworks() {
            const db = firebase.database();
            const snap = await db.ref("pages/210/artworks").once("value");
            this.artworks = snap.val() || [];
        },

        addArtwork() {
            this.artworks.push({ label: "", audio: "" });
        },

        removeArtwork(index) {
            this.artworks.splice(index, 1);
        },

        moveUp(index) {
            if (index === 0) return;
            const artworks = this.artworks;
            [artworks[index - 1], artworks[index]] = [artworks[index], artworks[index - 1]];
        },

        moveDown(index) {
            if (index === this.artworks.length - 1) return;
            const artworks = this.artworks;
            [artworks[index], artworks[index + 1]] = [artworks[index + 1], artworks[index]];
        },

        async saveAll() {
            this.saving = true;
            try {
                const db = firebase.database();
                await db.ref("pages/210/artworks").set(this.artworks);
                this.toast = true;
                setTimeout(() => { this.toast = false; }, 3000);
            } catch (e) {
                alert("Erreur lors de la sauvegarde : " + e.message);
            }
            this.saving = false;
        },
    },
};
</script>

<style scoped>
* { box-sizing: border-box; }

.pa-root {
    min-height: 100vh;
    background: #f0f4f8;
    font-family: system-ui, sans-serif;
    padding-bottom: 60px;
}

.pa-center {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pa-lock-card {
    text-align: center;
    background: white;
    padding: 40px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.pa-btn-google {
    margin-top: 12px;
    background: #63C4C3;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-weight: 600;
    cursor: pointer;
}

.pa-btn-google:disabled {
    opacity: 0.6;
    cursor: default;
}

.pa-error {
    color: #c0392b;
    font-size: 13px;
    margin-top: 10px;
}

.pa-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e0e0e0;
    border-top-color: #63C4C3;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.pa-body {
    max-width: 720px;
    margin: 0 auto;
    padding: 24px 16px;
}

.pa-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 8px;
}

.pa-title {
    font-size: 22px;
    font-weight: 700;
}

.pa-header-actions {
    display: flex;
    gap: 10px;
}

.pa-btn-preview, .pa-btn-save, .pa-btn-add {
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
}

.pa-btn-preview {
    background: white;
    border: 1px solid #ccc;
    color: #333;
}

.pa-btn-save {
    background: #63C4C3;
    color: white;
}

.pa-btn-save:disabled {
    opacity: 0.6;
    cursor: default;
}

.pa-hint {
    color: #666;
    font-size: 14px;
    margin-bottom: 16px;
}

.pa-debug {
    color: #999;
    font-size: 12px;
    font-family: monospace;
    margin-bottom: 16px;
}

.pa-toast {
    background: #d4f7d4;
    color: #14532d;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-weight: 600;
}

.pa-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.pa-card {
    display: flex;
    align-items: center;
    gap: 14px;
    background: white;
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
}

.pa-order {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;
}

.pa-btn-order {
    background: #eee;
    border: none;
    border-radius: 6px;
    width: 28px;
    height: 24px;
    font-size: 11px;
    cursor: pointer;
    color: #555;
}

.pa-btn-order:disabled {
    opacity: 0.35;
    cursor: default;
}

.pa-fields {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.pa-fields label {
    font-size: 11px;
    color: #888;
    margin-top: 4px;
}

.pa-fields input {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 14px;
}

.pa-btn-remove {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    flex-shrink: 0;
}

.pa-btn-add {
    margin-top: 18px;
    width: 100%;
    background: white;
    border: 2px dashed #63C4C3;
    color: #63C4C3;
}
</style>
